import { Box, Button, IconButton, MenuItem, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import { HttpFunction } from '../../../types/FunctionApp'

const CONTENT_TYPES = [
  'application/json',
  'application/xml',
  'text/plain',
  'application/x-www-form-urlencoded',
]

interface RequestTabProps {
  func: HttpFunction
  routeParams: Record<string, string>
  onRouteParamChange: (name: string, value: string) => void
  headers: { key: string; value: string }[]
  onHeaderChange: (index: number, field: 'key' | 'value', val: string) => void
  onAddHeader: () => void
  onRemoveHeader: (index: number) => void
  body: string
  onBodyChange: (body: string) => void
  contentType: string
  onContentTypeChange: (contentType: string) => void
  onExecute: () => void
  isExecuting: boolean
}

const RequestTab = ({
  func,
  routeParams,
  onRouteParamChange,
  headers,
  onHeaderChange,
  onAddHeader,
  onRemoveHeader,
  body,
  onBodyChange,
  contentType,
  onContentTypeChange,
  onExecute,
  isExecuting,
}: RequestTabProps) => {
  const { t } = useTranslation()

  const hasRequiredParamsMissing = func.routeParameters
    .filter((p) => p.required)
    .some((p) => !routeParams[p.name]?.trim())

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
      {func.routeParameters.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('command.routeParameters', 'Route Parameters')}
          </Typography>
          {func.routeParameters.map((param) => (
            <TextField
              key={param.name}
              size="small"
              fullWidth
              label={`${param.name}${param.required ? ' *' : ''}`}
              required={param.required}
              value={routeParams[param.name] ?? ''}
              onChange={(e) => onRouteParamChange(param.name, e.target.value)}
              sx={{ mb: 1 }}
            />
          ))}
        </Box>
      )}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">{t('command.headers', 'Headers')}</Typography>
          <IconButton size="small" onClick={onAddHeader}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        {headers.map((header, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField
              size="small"
              placeholder="Key"
              value={header.key}
              onChange={(e) => onHeaderChange(i, 'key', e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              placeholder="Value"
              value={header.value}
              onChange={(e) => onHeaderChange(i, 'value', e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={() => onRemoveHeader(i)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('command.body', 'Body')}
        </Typography>
        <TextField
          select
          size="small"
          fullWidth
          value={contentType}
          onChange={(e) => onContentTypeChange(e.target.value)}
          sx={{ mb: 1 }}
        >
          {CONTENT_TYPES.map((ct) => (
            <MenuItem key={ct} value={ct}>{ct}</MenuItem>
          ))}
        </TextField>
        <TextField
          multiline
          fullWidth
          minRows={6}
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          placeholder={contentType === 'application/json' ? '{}' : ''}
          sx={{ fontFamily: 'monospace' }}
        />
      </Box>

      <Button
        variant="contained"
        color="success"
        onClick={onExecute}
        disabled={isExecuting || hasRequiredParamsMissing}
        fullWidth
      >
        {isExecuting ? t('command.executing', 'Executing...') : t('command.execute', 'Execute')}
      </Button>
    </Box>
  )
}

export default RequestTab
